import { html, nothing } from 'lit';

import type { Channel } from '../../utils/get-channels-from-pypes';
import { stopRowKeyActivation } from '../../utils/row-key-activation';
import type { ChannelMenuItem } from './channel-row-actions';

/** Every string here is called with its English `defaultValue`. */
type Translate = (
  key: string,
  options: { defaultValue: string; phone?: string }
) => string;

export type ChannelRowAction = ChannelMenuItem['action'] | 'preview';

export interface RenderChannelRowActionsArgs {
  channel: Channel;
  /** From `getChannelMenuItems`; empty means no actions cell at all. */
  items: ChannelMenuItem[];
  /** From `getChannelPhoneNumber`. */
  phone: string | undefined;
  t: Translate;
  onAction: (action: ChannelRowAction, value?: string) => void;
}

/**
 * The Channels table's actions cell: Preview (or the voice channel's phone),
 * and the kebab with its menu. Lit, because it renders inside `ps-data-table`'s
 * row; a plain function so its keyboard wiring can be pinned without the page.
 */
export const renderChannelRowActions = ({
  channel,
  items,
  phone,
  t,
  onAction,
}: RenderChannelRowActionsArgs) => {
  // Contact Center (vaapi) channels are managed in Contact Center:
  // no preview, no menu.
  if (!items.length) return html``;

  const kebabId = `ps-channel-row-kebab-${channel.id}`;
  // The row itself navigates to the channel — on click AND on Enter / Space
  // (its keydown handler) — so neither may bubble out of here. Escape MUST:
  // the open dropdown closes on a document-level keydown, and stopping every
  // key kept this menu open (release 29817). Same rule as the solution boxes.
  const stopClick = (e: Event) => e.stopPropagation();

  return html`<ps-stack
      alignItems="center"
      justifyContent="end"
      gutter="xsmall"
      nowrap
      @click=${stopClick}
      @keydown=${stopRowKeyActivation}
    >
      ${channel.type === 'twilio_voice'
        ? phone
          ? html`<ps-button
              test-id=${`ps-channel-row-phone-${channel.id}`}
              size="small"
              @click=${() => onAction('copyPhone', phone)}
            >
              <ps-icon name="call" size="small" slot="prefix"></ps-icon>
              ${phone}
            </ps-button>`
          : nothing
        : html`<ps-button
            test-id=${`ps-channel-row-preview-${channel.id}`}
            size="small"
            @click=${() => onAction('preview')}
          >
            <ps-icon name="external-link" size="small" slot="prefix"></ps-icon>
            ${t('pro-studio/channels:preview', {
              defaultValue: 'Preview',
            })}
          </ps-button>`}
      <ps-icon-button
        test-id=${`ps-channel-row-kebab-${channel.id}`}
        name="kebab"
        size="small"
        label=${`${t('pro-studio/channels:menu.actionsFor', {
          defaultValue: 'Actions for',
        })} ${channel.name}`}
        has-popup="menu"
        id=${kebabId}
      ></ps-icon-button>
    </ps-stack>
    <ps-dropdown trigger=${kebabId} placement="bottom-end">
      <ps-menu @click=${stopClick} @keydown=${stopRowKeyActivation}>
        ${items.map(
          (item: ChannelMenuItem) =>
            html`<ps-menu-item
              test-id=${`ps-channel-row-menu-${item.action}-${channel.id}`}
              variant=${item.variant ?? nothing}
              @click=${() => onAction(item.action, item.value)}
            >
              <ps-icon name=${item.icon} size="small" slot="prefix"></ps-icon>
              ${t(`pro-studio/channels:${item.labelKey}`, {
                defaultValue: item.defaultLabel,
                phone,
              })}
            </ps-menu-item>`
        )}
      </ps-menu>
    </ps-dropdown>`;
};
